import {html, LitElement, PropertyValues} from "lit";
import {customElement, property} from "lit/decorators.js";
import {styles} from './sp-spot-transfer-indicator-dialog.styles';
import {repeat} from "lit-html/directives/repeat.js";
import {when} from "lit/directives/when.js";
import {translate, TranslateService} from "@stefanholzapfel/lit-translate";
import {styleMap} from "lit-html/directives/style-map.js";
import {classMap} from "lit-html/directives/class-map.js";
import {transparentButton} from '../../../styles/buttons/transparent-button.styles';
import {SpImageService} from "../../../services/sp-image.service";
import {SpSpotTransferIndicatorAction, SpTransferringSpot, SpTransferStatus} from "../sp-spot-transfer-indicator.types";
import {getIcon} from "../../../styles/icons";

@customElement('sp-spot-transfer-indicator-dialog')
export class SpSpotTransferIndicatorDialog extends LitElement {

    @property()
    transferringSpots: SpTransferringSpot[];
    @property()
    clickActionHandler: (action: SpSpotTransferIndicatorAction, ids: string[]) => void;
    @property({type: Boolean})
    allowQueue;

    imageObjectUrls = new Map<string, string>();
    resizeObserver;
    latestWidth = 0;
    // The width in px where the success text should be exchanged for "100%"
    switchWidth = 370;

    static styles = [
        styles,
        transparentButton
    ];

    protected render() {
        return html`
            ${when(this.transferringSpots.length === 0, () => translate('UPLOADS_EMPTY'))}
            ${repeat(this.transferringSpots, spot => spot.id, (value, index) => this.getRow(value))}
        `;
    }

    protected firstUpdated(_changedProperties: PropertyValues) {
        super.firstUpdated(_changedProperties);
        // Only needed for legacy reasons in the spotteron-app, which uses fastclick.js and cannot set classes for nested components
        this.shadowRoot.host.classList.add('needsclick');
        this.resizeObserver = new ResizeObserver(entries => {
            if (this.offsetWidth > this.switchWidth && this.latestWidth <= this.switchWidth ||
                this.offsetWidth <= this.switchWidth && this.latestWidth > this.switchWidth) {
                this.requestUpdate();
            }
            this.latestWidth = this.offsetWidth;
        });
        this.resizeObserver.observe(this);
    }

    getRow(spot: SpTransferringSpot) {
        const values = this.getValues(spot);
        const isFinished = spot.status === SpTransferStatus.Finished;
        return html`
            <div class="row-container">
                <div class="image" 
                     style=${styleMap({
                         'background-image': this.getUploadImage(spot),
                         cursor: isFinished ? 'pointer' : 'unset'
                     })}
                     @click=${ () => { this.imageClick(spot) } }></div>
                <div class="indicator">
                    <sl-progress-bar
                            class=${ classMap({
                                inactive: spot.status === SpTransferStatus.Aborted || spot.status === SpTransferStatus.Finished || spot.status === SpTransferStatus.Paused,
                                erroneous: spot.status === SpTransferStatus.Erroneous}) }
                            .value=${spot.progress * 100}
                            ?indeterminate=${spot.status === SpTransferStatus.PostProcessing}>${ 
                                when(![SpTransferStatus.PostProcessing, SpTransferStatus.Erroneous, SpTransferStatus.Aborted].includes(spot.status), () => values.text) }</sl-progress-bar>
                    <span class="text">${ when([SpTransferStatus.PostProcessing, SpTransferStatus.Erroneous, SpTransferStatus.Aborted].includes(spot.status), () => values.text) }</span>
                    <span class="clear">
                        ${ this.createButton([SpTransferStatus.Finished, SpTransferStatus.Aborted].includes(spot.status) ? 'remove' : 'abort', spot) }
                    </span>
                </div>
                ${ when(values.button, () => values.button) }
            </div>`;
    }

    getUploadImage(spot: SpTransferringSpot): string {
        if (spot.image) {
            let url;
            if ('path' in spot.image) {
                url = SpImageService.buildUrl(spot.image.spec.type, 'thumbs', spot.image.path, 'x200', );
            } else {
                url = this.imageObjectUrls.get(spot.id);
                if (!url) {
                    url = URL.createObjectURL(spot.image.blob);
                    this.imageObjectUrls.set(spot.id, url);
                }
            }
            return `url(${url})`;
        } else {
            return `url(${ SpImageService.getPlaceholderUrl('spots')})`;
        }
    }

    getValues(spot: SpTransferringSpot) {
        switch(spot.status) {
            case SpTransferStatus.NotStarted:
            case SpTransferStatus.Executing:
                return {
                    button: this.createButton('pause', spot, spot.status === SpTransferStatus.NotStarted),
                    text: translate('UPLOADS_IN_PROGRESS', {percentage: this.getPercentageHumanReadable(spot)})
                };
            case SpTransferStatus.PostProcessing:
                return {
                    button: this.createButton('pause', spot, true),
                    text: translate('UPLOADS_POSTPROCESSING')
                };
            case SpTransferStatus.Paused:
                return {
                    button: this.createButton('resume', spot),
                    text: translate('UPLOADS_PAUSED', {percentage: this.getPercentageHumanReadable(spot)})
                };
            case SpTransferStatus.Erroneous:
                let errorMsg = TranslateService.translate(spot.error);
                return {
                    button: this.createButton('retry', spot),
                    text: translate('UPLOADS_ERROR', { error: errorMsg })
                };
            case SpTransferStatus.Finished:
                return {
                    button: this.createButton('browse', spot),
                    text: this.offsetWidth > this.switchWidth ? translate('UPLOADS_FINISHED') : translate('UPLOADS_IN_PROGRESS', {percentage: this.getPercentageHumanReadable(spot) })
                };
            case SpTransferStatus.Aborted:
                return {
                    ...this.allowQueue && { button: this.createButton('queue', spot) },
                    text: translate('UPLOADS_ABORTED')
                };
        }
    }

    getPercentageHumanReadable(spot) {
        return Math.floor(spot.progress * 100).toString();
    }

    createButton(action: SpSpotTransferIndicatorAction, spot: SpTransferringSpot, disabled = false) {
        return html`<sp-button
                        class="transparent-button"
                        @click=${ () => { this.clickActionHandler(action, [ spot.id ]); } }
                        ?disabled=${ disabled }
                        .icon="url(${ this.getIconFromAction(action, spot) })"></sp-button>`;
    }

    getIconFromAction(action: SpSpotTransferIndicatorAction, spot: SpTransferringSpot) {
        switch(action) {
            case 'pause': return getIcon('pause', { color: 'grey-dark' });
            case 'resume': return getIcon('resume', { color: 'grey-dark' });
            case 'abort': return getIcon('close', { size: 'x128' });
            case 'retry': return getIcon('retry', { color: 'grey-dark'});
            case 'queue': return getIcon('hourglass', { color: 'grey-dark'});
            case 'remove':
                return spot.status === SpTransferStatus.Finished ?
                    getIcon('check', { size: 'x128' }) : getIcon('close', { size: 'x128' });
            case 'browse': return getIcon('eye', { color: 'grey-dark' });
        }
    }

    imageClick(spot: SpTransferringSpot) {
        if (spot.status === SpTransferStatus.Finished) {
            this.clickActionHandler('browse', [spot.id]);
        }
    }

    connectedCallback() {
        super.connectedCallback();
        if (this.resizeObserver)
            this.resizeObserver.observe(this);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        for (const [key, objectUrl] of this.imageObjectUrls.entries()) {
            URL.revokeObjectURL(objectUrl);
        }
        if (this.resizeObserver)
            this.resizeObserver.disconnect();
    }
}

